import { useCallback, useEffect, useRef } from 'react';

/**
 * Ennyi ido utan szamit egy erintes hosszu nyomasnak.
 *
 * A rendszer sajat menui 400-500 ms korul nyilnak; ennel rovidebbnel a sima
 * koppintas is "hosszu nyomas" lenne, hosszabbnal pedig az ember elengedi,
 * mielott barmi tortenne.
 */
const LONG_PRESS_MS = 420;

/**
 * Ennyit meg szabad csusznia az ujjnak.
 *
 * Gorgetes kozben az elso nehany keppont mozgas elkerulhetetlen, es nem
 * szabad, hogy egy lista-gorgetes menut nyisson.
 */
const MOVE_TOLERANCE_PX = 12;

export interface LongPressHandlers {
  onPointerDown: (event: React.PointerEvent) => void;
  onPointerMove: (event: React.PointerEvent) => void;
  onPointerUp: (event: React.PointerEvent) => void;
  onPointerCancel: (event: React.PointerEvent) => void;
  onContextMenu: (event: React.MouseEvent) => void;
  onClickCapture: (event: React.MouseEvent) => void;
}

/**
 * Hosszu nyomas erintessel.
 *
 * Egerrel NEM sul el: ott a hover-sav a jarhato ut, es egy veletlen "lenyomva
 * felejtett" egergomb nem nyithat menut.
 *
 * Ket dolgot kell meg elintezni, kulonben a menu mellett a bongeszo sajat
 * viselkedese is elindul:
 *
 *  - `onContextMenu` letiltasa: Androidon a hosszu nyomas a rendszer
 *    "masolas / megosztas" savjat hozna elo ugyanabban a pillanatban,
 *  - a nyomast koveto kattintas elnyelese: enelkul egy linken tartott ujj
 *    egyszerre nyitna meg a menut ES a linket.
 */
export function useLongPress(onLongPress: () => void, enabled = true): LongPressHandlers {
  const timer = useRef<number | null>(null);
  const origin = useRef<{ x: number; y: number } | null>(null);
  const fired = useRef(false);

  // A visszahivas minden rendereskor uj - refben tartva a kezelok stabilak
  // maradnak, es nem kell az egesz sort ujrakotni miatta.
  const callback = useRef(onLongPress);
  callback.current = onLongPress;

  const cancel = useCallback(() => {
    if (timer.current !== null) {
      window.clearTimeout(timer.current);
      timer.current = null;
    }
    origin.current = null;
  }, []);

  useEffect(() => cancel, [cancel]);

  const onPointerDown = useCallback(
    (event: React.PointerEvent) => {
      if (!enabled || event.pointerType === 'mouse') return;

      cancel();
      fired.current = false;
      origin.current = { x: event.clientX, y: event.clientY };

      timer.current = window.setTimeout(() => {
        timer.current = null;
        origin.current = null;
        fired.current = true;
        // Rovid rezges: visszajelzes arrol, hogy a nyomas "megfogott".
        if (typeof navigator.vibrate === 'function') navigator.vibrate(8);
        callback.current();
      }, LONG_PRESS_MS);
    },
    [cancel, enabled],
  );

  const onPointerMove = useCallback(
    (event: React.PointerEvent) => {
      const start = origin.current;
      if (!start) return;

      const dx = event.clientX - start.x;
      const dy = event.clientY - start.y;
      if (Math.hypot(dx, dy) > MOVE_TOLERANCE_PX) cancel();
    },
    [cancel],
  );

  const onContextMenu = useCallback(
    (event: React.MouseEvent) => {
      if (enabled) event.preventDefault();
    },
    [enabled],
  );

  const onClickCapture = useCallback((event: React.MouseEvent) => {
    if (!fired.current) return;
    fired.current = false;
    event.preventDefault();
    event.stopPropagation();
  }, []);

  return {
    onPointerDown,
    onPointerMove,
    onPointerUp: cancel,
    onPointerCancel: cancel,
    onContextMenu,
    onClickCapture,
  };
}
